import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import router from '@/router'
import { useSubscriptionStore } from '@/stores/subscription'
import { logError, logWarn } from '@/utils/logger'
import { useFeatureGate } from '@/composables/useFeatureGate'

export interface User {
  user_id: string
  email: string
  phone: string
  role: 'admin_vendor' | 'sales_vendor' | 'customer' | 'super_admin'
  business_name: string
  business_id?: string
  is_super_admin?: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  phone: string
  password: string
  business_name: string
  role: 'admin_vendor' | 'sales_vendor' | 'customer'
  plan?: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const user = ref<User | null>(null)
  const loading = ref(false)
  const authNotice = ref<string | null>(sessionStorage.getItem('auth_notice'))
  const initialized = ref(false)
  let initPromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdminVendor = computed(() => user.value?.role === 'admin_vendor')
  const isSalesVendor = computed(() => user.value?.role === 'sales_vendor')
  const isCustomer = computed(() => user.value?.role === 'customer')
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin' || user.value?.is_super_admin === true)
  const isVendor = computed(() => isAdminVendor.value || isSalesVendor.value)

  const extractApiError = (err: unknown, fallbackMessage: string): Record<string, unknown> => {
    if (typeof err === 'object' && err !== null) {
      const maybeError = err as { response?: { data?: unknown } }
      const responseData = maybeError.response?.data
      if (typeof responseData === 'object' && responseData !== null) {
        return responseData as Record<string, unknown>
      }
    }

    if (err instanceof Error && err.message) {
      return { error: err.message }
    }

    return { error: fallbackMessage }
  }

  const getStatusCode = (err: unknown): number | null => {
    if (typeof err === 'object' && err !== null) {
      const maybeError = err as { response?: { status?: number } }
      return maybeError.response?.status ?? null
    }

    return null
  }

  const setAuthNotice = (message: string | null) => {
    authNotice.value = message

    if (message) {
      sessionStorage.setItem('auth_notice', message)
    } else {
      sessionStorage.removeItem('auth_notice')
    }
  }

  const clearAuthState = () => {
    token.value = null
    user.value = null
    localStorage.removeItem('auth_token')
    useFeatureGate(false).resetGates()
    try {
      useSubscriptionStore().$reset()
    } catch {
      // Ignore subscription store reset failures during auth teardown
    }
    initialized.value = true
  }

  const redirectToLogin = async (message?: string) => {
    if (message) {
      setAuthNotice(message)
    }

    if (router.currentRoute.value.path === '/login') {
      return
    }

    await router.replace({ path: '/login' })
  }

  const consumeAuthNotice = () => {
    const notice = authNotice.value
    setAuthNotice(null)
    return notice
  }

  async function login(credentials: LoginCredentials) {
    loading.value = true
    try {
      const subscriptionStore = useSubscriptionStore()
      subscriptionStore.$reset()
      const response = await api.post('/auth/login', credentials)
      const { token: authToken, user: userData } = response.data
      
      token.value = authToken
      user.value = userData
      localStorage.setItem('auth_token', authToken)
      const featureGate = useFeatureGate(false)
      featureGate.resetGates()
      setAuthNotice(null)
      initialized.value = true
      await featureGate.fetchGates()

      if (userData?.role === 'admin_vendor' || userData?.role === 'sales_vendor') {
        try {
          await subscriptionStore.fetchStatus()
        } catch (error) {
          logWarn('AUTH_STORE', 'LOGIN', 'Subscription status fetch failed after successful login')
        }
      }
      
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Login failed')
    } finally {
      loading.value = false
    }
  }

  async function register(userData: RegisterData) {
    loading.value = true
    try {
      const response = await api.post('/auth/register', userData)
      const { token: authToken, user: registeredUser } = response.data

      if (authToken && registeredUser) {
        token.value = authToken
        user.value = registeredUser
        localStorage.setItem('auth_token', authToken)
        const featureGate = useFeatureGate(false)
        featureGate.resetGates()
        setAuthNotice(null)
        initialized.value = true
        await featureGate.fetchGates()

        try {
          await useSubscriptionStore().fetchStatus()
        } catch {
          logWarn('AUTH_STORE', 'REGISTER', 'Subscription status fetch failed after registration')
        }
      }

      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Registration failed')
    } finally {
      loading.value = false
    }
  }

  async function logout(options: { skipRequest?: boolean; redirectMessage?: string | null; redirect?: boolean } = {}) {
    const { skipRequest = false, redirectMessage = null, redirect = true } = options
    loading.value = true
    try {
      if (token.value && !skipRequest) {
        await api.post('/auth/logout')
      }
    } catch (error: unknown) {
      if (getStatusCode(error) !== 401) {
        logError('AUTH_STORE', 'LOGOUT', 'Logout error', {}, error as Error)
      }
    } finally {
      clearAuthState()
      loading.value = false
      if (redirect) {
        await redirectToLogin(redirectMessage ?? undefined)
      }
    }
  }

  async function handleUnauthorized(
    message = 'Your session expired. Please sign in again.',
    options: { redirect?: boolean } = {},
  ) {
    const redirect = options.redirect ?? router.currentRoute.value.meta.requiresAuth === true
    logWarn('AUTH_STORE', 'UNAUTHORIZED', message)
    await logout({
      skipRequest: true,
      redirect,
      redirectMessage: redirect ? message : null,
    })
  }

  async function fetchUser() {
    if (!token.value) {
      logWarn('AUTH_STORE', 'FETCH_USER', 'No token available, skipping fetchUser')
      initialized.value = true
      return
    }

    loading.value = true
    try {
      const response = await api.get('/auth/me')
      user.value = response.data.user
      
      // Sync subscription info if available
      if (response.data.subscription) {
        try {
          const subscriptionStore = useSubscriptionStore()
          subscriptionStore.setStatus(response.data.subscription)
        } catch (e) {
          // Subscription store may not be available
        }
      }
    } catch (error: unknown) {
      if (getStatusCode(error) === 401) {
        await handleUnauthorized()
        return
      }

      logError('AUTH_STORE', 'FETCH_USER', 'Fetch user error', {}, error as Error)
    } finally {
      initialized.value = true
      loading.value = false
    }
  }

  async function ensureInitialized() {
    if (initialized.value) {
      return
    }

    if (!token.value) {
      initialized.value = true
      return
    }

    if (user.value) {
      initialized.value = true
      return
    }

    if (!initPromise) {
      initPromise = fetchUser().finally(() => {
        initPromise = null
      })
    }

    await initPromise
  }

  async function forgotPassword(email: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Password reset failed')
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(token: string, newPassword: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      })
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Password reset failed')
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(verificationToken: string) {
    loading.value = true
    try {
      const response = await api.get(`/auth/verify-email/${verificationToken}`)
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Email verification failed')
    } finally {
      loading.value = false
    }
  }

  async function resendVerificationEmail(email: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/resend-verification-email', { email })
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Unable to resend verification email')
    } finally {
      loading.value = false
    }
  }

  return {
    token,
    user,
    loading,
    initialized,
    isAuthenticated,
    isAdminVendor,
    isSalesVendor,
    isCustomer,
    isSuperAdmin,
    isVendor,
    login,
    register,
    logout,
    handleUnauthorized,
    fetchUser,
    ensureInitialized,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    authNotice,
    consumeAuthNotice,
  }
})
